'use client';

import {
  useContext, useState, useRef, useEffect,
} from 'react';
import { useTranslation } from 'next-i18next';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { NeuroIcon } from '@components/common/Icons';
import { useAppSelector } from 'src/app';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { cn } from '@utils/cn';
import { TwoColumnLayoutContext } from '@components/layout/Layouts/TwoColumnLayout/TwoColumnLayoutContext';
import { ProfileAvatar } from '@components/layout/Header/MainNavbar/profile/ProfileAvatar';
import { useBurgerMenuData } from '@components/layout/Header/MainNavbar/data/useBurgerMenuData';
import { SidebarNavPopup } from './partials/SidebarNavPopup';

interface SidebarAccountRowProps {
  className?: string;
}

export const SidebarAccountRow = ({ className }: SidebarAccountRowProps) => {
  const { t } = useTranslation('common');
  const layoutCtx = useContext(TwoColumnLayoutContext);
  const isLeftOpen = layoutCtx?.isLeftOpen ?? true;
  const headerInfo = useAppSelector(getUserHeaderInfo);
  const [isOpen, setIsOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // Warm the SWR cache for the popup menu while the row is mounted, so the
  // menu items are ready instantly on first open instead of fetching on click.
  useBurgerMenuData();

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (rowRef.current?.contains(target)) return;
      // Portaled flyouts (e.g. the Hire submenu) live outside rowRef but are
      // logically part of the popup — don't treat clicks on them as "outside".
      if (target.closest('[data-sidebar-flyout]')) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const isCompany = headerInfo?.hasBusinessWallet;
  const name = isCompany
    ? headerInfo?.name || t('account.label.myaccount')
    : headerInfo?.candidateName || t('account.label.myaccount');
  const credits = headerInfo?.walletCredits;
  const totalCredits = headerInfo?.ordersNotificationData?.totalCredits ?? 0;
  const discount = headerInfo?.discountPercentage ?? 0;

  const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

  return (
    <div
      ref={rowRef}
      className={cn('relative shrink-0 bg-surface px-2 py-2', className)}
    >
      {/* Pill-styled trigger, visually matching the SidebarContextSwitch toggle above it. */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left transition-colors',
          'hover:bg-surface-150 focus:outline-none focus:bg-surface-150',
          !isLeftOpen && 'justify-center px-0',
        )}
      >
        <ProfileAvatar headerInfo={headerInfo} />
        {isLeftOpen && (
          <>
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <span className="truncate text-sm font-semibold text-ink">{name}</span>
              {/* Credits / discount line is hidden until headerInfo is loaded so
                  we don't flash "0" alongside the NeuroIcon while waiting. */}
              {headerInfo && (
                <div className="flex items-baseline gap-0.5 text-sm font-bold text-ink">
                  <span className="flex items-baseline gap-0.5">
                    {credits ?? 0}
                    <NeuroIcon className="size-1.75" />
                  </span>
                  {totalCredits > 0 && (
                    <span className="whitespace-nowrap px-1 text-xs font-thin text-ink">
                      (
                      {totalCredits}
                      {' '}
                      {t('navbar.topup_pending')}
                      )
                    </span>
                  )}
                  {discount > 0 && totalCredits === 0 && (
                    <span className="ml-1 rounded-md bg-secondary/20 px-1 py-0.5 text-pico font-thin text-secondary">
                      {discount}
                      % OFF
                    </span>
                  )}
                </div>
              )}
            </div>
            <ChevronIcon className="size-4 shrink-0 text-ink/65" />
          </>
        )}
      </button>

      {isOpen && (
        <SidebarNavPopup
          isLeftOpen={isLeftOpen}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
